import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Checkbox,
  FormControlLabel,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { Formik } from "formik";
import * as yup from "yup";
import { GraphQLClient } from "graphql-request";

import Header from "../../components/Header";
import ActionButton from "../../components/ActionButton";
import { useTenant } from "../../context/TenantContext";
import { useSnackbar } from "../../components/snackbarProvider.jsx";

import { graphqlEndpoint } from "../../config";
import { UserRole } from "../../constants/userRoles.ts";
import { READ_TENANT_USERS_BY_TENANT } from "../../graph/tenantUsers/queries";
import { READ_TENANT_LEAVE_YEARS } from "../../graph/holidayManagement/queries";
import { READ_STAFF_WORK_PATTERNS } from "../../graph/staffWorkPatterns/queries";
import { CREATE_STAFF_WORK_PATTERN } from "../../graph/staffWorkPatterns/mutations";

const client = new GraphQLClient(graphqlEndpoint);

const daysOfWeek = [
  "MONDAY",
  "TUESDAY",
  "WEDNESDAY",
  "THURSDAY",
  "FRIDAY",
  "SATURDAY",
  "SUNDAY",
];

const allowedTenantRoles = [
  UserRole.TENANT_ADMIN,
  UserRole.TENANT_STAFF,
];

const standardDays = daysOfWeek.map((dayOfWeek) => {
  const isWorkingDay = [
    "MONDAY",
    "TUESDAY",
    "WEDNESDAY",
    "THURSDAY",
    "FRIDAY",
  ].includes(dayOfWeek);

  return {
    dayOfWeek,
    isWorkingDay,
    startTime: isWorkingDay ? "09:00" : "",
    endTime: isWorkingDay ? "17:30" : "",
    unpaidBreakMinutes: isWorkingDay ? 30 : 0,
  };
});

const validationSchema = yup.object({
  staffTenantUserID: yup.string().required("Select a staff member"),
  leaveYearID: yup.string().required("Select a leave year"),
  name: yup.string().trim().required("Pattern name is required"),
});

const formatDayLabel = (day) =>
  day.charAt(0) + day.slice(1).toLowerCase();

const minutesFromTime = (time) => {
  const [hours, minutes] = time.split(":").map(Number);
  return hours * 60 + minutes;
};

const CreateStaffWorkPattern = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { tenantId, tenantName } = useTenant();
  const { setSnackbar } = useSnackbar();

  const [staffUsers, setStaffUsers] = useState([]);
  const [leaveYears, setLeaveYears] = useState([]);
  const [initialValues, setInitialValues] = useState(null);
  const [loading, setLoading] = useState(true);

  const redirectPath =
    location.state?.redirectPath || "/manageStaffWorkPatterns";

  useEffect(() => {
    const fetchOptions = async () => {
      if (!tenantId) {
        setStaffUsers([]);
        setLeaveYears([]);
        setLoading(false);
        return;
      }

      try {
        const [usersData, leaveYearsData] = await Promise.all([
          client.request(READ_TENANT_USERS_BY_TENANT, {
            tenantId,
          }),
          client.request(READ_TENANT_LEAVE_YEARS, {
            tenantID: tenantId,
          }),
        ]);

        const users = (usersData.readTenantUsersByTenant || [])
          .filter(
            (tenantUser) =>
              !tenantUser.archivedAt &&
              allowedTenantRoles.includes(tenantUser.user?.role)
          )
          .map((tenantUser) => {
            const globalUser = tenantUser.user;
            const fallbackName = `${globalUser?.firstName || ""} ${
              globalUser?.lastName || ""
            }`.trim();

            return {
              id: tenantUser.id,
              name:
                tenantUser.displayName ||
                fallbackName ||
                globalUser?.email ||
                tenantUser.userId,
              email: globalUser?.email || "",
            };
          });

        const openLeaveYears = (
          leaveYearsData.readTenantLeaveYears || []
        ).filter((leaveYear) => leaveYear.status === "OPEN");

        setStaffUsers(users);
        setLeaveYears(openLeaveYears);

        const selectedLeaveYearID =
          location.state?.leaveYearID &&
          openLeaveYears.some(
            (leaveYear) => leaveYear.id === location.state.leaveYearID
          )
            ? location.state.leaveYearID
            : openLeaveYears[0]?.id || "";

        const selectedStaffID =
          location.state?.staffTenantUserID &&
          users.some((staff) => staff.id === location.state.staffTenantUserID)
            ? location.state.staffTenantUserID
            : "";

        setInitialValues({
          staffTenantUserID: selectedStaffID,
          leaveYearID: selectedLeaveYearID,
          name: "Standard 9–5:30 pattern",
          days: standardDays,
        });
      } catch (err) {
        console.error("Failed to load staff pattern options:", err);
        setSnackbar({
          open: true,
          severity: "error",
          message: "Failed to load staff and leave year options",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchOptions();
  }, [tenantId, location.state, setSnackbar]);

  const handleSubmit = async (values) => {
    const workingDays = values.days.filter((day) => day.isWorkingDay);

    if (workingDays.length === 0) {
      setSnackbar({
        open: true,
        severity: "error",
        message: "Select at least one working day",
      });
      return;
    }

    for (const day of workingDays) {
      if (!day.startTime || !day.endTime) {
        setSnackbar({
          open: true,
          severity: "error",
          message: `Enter start and end times for ${formatDayLabel(day.dayOfWeek)}`,
        });
        return;
      }

      const scheduledMinutes =
        minutesFromTime(day.endTime) - minutesFromTime(day.startTime);
      const unpaidBreakMinutes = Number(day.unpaidBreakMinutes);

      if (
        scheduledMinutes <= 0 ||
        unpaidBreakMinutes < 0 ||
        unpaidBreakMinutes >= scheduledMinutes
      ) {
        setSnackbar({
          open: true,
          severity: "error",
          message: `Check the times and unpaid break for ${formatDayLabel(day.dayOfWeek)}`,
        });
        return;
      }
    }

    const input = {
      tenantID: tenantId,
      staffTenantUserID: values.staffTenantUserID,
      leaveYearID: values.leaveYearID,
      name: values.name.trim(),
      days: values.days.map((day) => ({
        dayOfWeek: day.dayOfWeek,
        isWorkingDay: day.isWorkingDay,
        startTime: day.isWorkingDay ? day.startTime : null,
        endTime: day.isWorkingDay ? day.endTime : null,
        unpaidBreakMinutes: day.isWorkingDay
          ? Number(day.unpaidBreakMinutes)
          : 0,
      })),
    };

    try {
      const existingData = await client.request(READ_STAFF_WORK_PATTERNS, {
        tenantID: tenantId,
        staffTenantUserID: input.staffTenantUserID,
        leaveYearID: input.leaveYearID,
      });

      if ((existingData.readStaffWorkPatterns || []).length > 0) {
        setSnackbar({
          open: true,
          severity: "warning",
          message:
            "A work pattern already exists for this staff member and leave year. Open it from the staff work patterns list to edit it.",
        });
        return;
      }

      await client.request(CREATE_STAFF_WORK_PATTERN, { input });

      setSnackbar({
        open: true,
        severity: "success",
        message: "Staff work pattern created successfully",
      });

      navigate(redirectPath, {
        state: { leaveYearID: input.leaveYearID },
      });
    } catch (err) {
      console.error("Failed to create staff work pattern:", err);
      setSnackbar({
        open: true,
        severity: "error",
        message: "Failed to create staff work pattern",
      });
    }
  };

  if (!tenantId) {
    return (
      <Box>
        <Header
          title="CREATE STAFF WORK PATTERN"
          subtitle="Select a tenant before creating a work pattern"
        />
        <Typography mt={3}>
          Use the tenant selector in the top bar, then return here.
        </Typography>
      </Box>
    );
  }

  if (loading) {
    return <Typography>Loading staff and leave years...</Typography>;
  }

  if (!leaveYears.length) {
    return (
      <Box>
        <Header
          title="CREATE STAFF WORK PATTERN"
          subtitle={`Create a work pattern for ${tenantName || "this tenant"}`}
        />
        <Typography mt={3}>
          This tenant has no open leave year. Create or open a leave year before
          adding staff work patterns.
        </Typography>
      </Box>
    );
  }

  if (!staffUsers.length) {
    return (
      <Box>
        <Header
          title="CREATE STAFF WORK PATTERN"
          subtitle={`Create a work pattern for ${tenantName || "this tenant"}`}
        />
        <Typography mt={3}>
          No active tenant staff members are available.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Header
        title="CREATE STAFF WORK PATTERN"
        subtitle={`Create a work pattern for ${tenantName || "this tenant"}`}
      />

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({
          values,
          errors,
          touched,
          handleBlur,
          handleChange,
          handleSubmit,
          setFieldValue,
          isSubmitting,
        }) => {
          const selectedLeaveYear = leaveYears.find(
            (leaveYear) => leaveYear.id === values.leaveYearID
          );

          return (
            <form onSubmit={handleSubmit}>
              <Box
                display="grid"
                gap="20px"
                gridTemplateColumns="repeat(4, minmax(0, 1fr))"
              >
                <TextField
                  select
                  label="Staff Member"
                  name="staffTenantUserID"
                  value={values.staffTenantUserID}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={
                    !!touched.staffTenantUserID &&
                    !!errors.staffTenantUserID
                  }
                  helperText={
                    touched.staffTenantUserID && errors.staffTenantUserID
                  }
                  sx={{ gridColumn: "span 2" }}
                >
                  {staffUsers.map((staff) => (
                    <MenuItem key={staff.id} value={staff.id}>
                      {staff.name}
                      {staff.email ? ` (${staff.email})` : ""}
                    </MenuItem>
                  ))}
                </TextField>

                <TextField
                  select
                  label="Leave Year"
                  name="leaveYearID"
                  value={values.leaveYearID}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={!!touched.leaveYearID && !!errors.leaveYearID}
                  helperText={touched.leaveYearID && errors.leaveYearID}
                  sx={{ gridColumn: "span 2" }}
                >
                  {leaveYears.map((leaveYear) => (
                    <MenuItem key={leaveYear.id} value={leaveYear.id}>
                      {leaveYear.startDate} to {leaveYear.endDate}
                    </MenuItem>
                  ))}
                </TextField>

                <TextField
                  label="Pattern Name"
                  name="name"
                  value={values.name}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={!!touched.name && !!errors.name}
                  helperText={touched.name && errors.name}
                  sx={{ gridColumn: "span 4" }}
                />

                {selectedLeaveYear && (
                  <Typography sx={{ gridColumn: "span 4" }}>
                    This pattern starts Monday of the current week, or at the
                    leave-year start if that is later. It runs through the day
                    before the leave year ends: {selectedLeaveYear.startDate} to{" "}
                    {selectedLeaveYear.endDate}.
                  </Typography>
                )}

                {values.days.map((day, index) => (
                  <Box
                    key={day.dayOfWeek}
                    sx={{
                      gridColumn: "span 2",
                      border: 1,
                      borderColor: "divider",
                      borderRadius: 1,
                      p: 2,
                    }}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={day.isWorkingDay}
                          onChange={(event) => {
                            const isWorkingDay = event.target.checked;

                            setFieldValue(
                              `days.${index}.isWorkingDay`,
                              isWorkingDay
                            );

                            if (isWorkingDay) {
                              setFieldValue(
                                `days.${index}.startTime`,
                                day.startTime || "09:00"
                              );
                              setFieldValue(
                                `days.${index}.endTime`,
                                day.endTime || "17:30"
                              );
                              setFieldValue(
                                `days.${index}.unpaidBreakMinutes`,
                                day.unpaidBreakMinutes || 30
                              );
                            } else {
                              setFieldValue(`days.${index}.startTime`, "");
                              setFieldValue(`days.${index}.endTime`, "");
                              setFieldValue(
                                `days.${index}.unpaidBreakMinutes`,
                                0
                              );
                            }
                          }}
                        />
                      }
                      label={formatDayLabel(day.dayOfWeek)}
                    />

                    {day.isWorkingDay && (
                      <Box display="grid" gap={2} mt={1}>
                        <TextField
                          label="Start Time"
                          name={`days.${index}.startTime`}
                          type="time"
                          value={day.startTime}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          InputLabelProps={{ shrink: true }}
                        />

                        <TextField
                          label="End Time"
                          name={`days.${index}.endTime`}
                          type="time"
                          value={day.endTime}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          InputLabelProps={{ shrink: true }}
                        />

                        <TextField
                          label="Unpaid Break (minutes)"
                          name={`days.${index}.unpaidBreakMinutes`}
                          type="number"
                          value={day.unpaidBreakMinutes}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          inputProps={{ min: 0 }}
                        />
                      </Box>
                    )}
                  </Box>
                ))}
              </Box>

              <Box display="flex" justifyContent="flex-end" mt="20px">
                <ActionButton
                  type="submit"
                  tone="success"
                  disabled={isSubmitting}
                >
                  Create Work Pattern
                </ActionButton>
              </Box>
            </form>
          );
        }}
      </Formik>
    </Box>
  );
};

export default CreateStaffWorkPattern;